import { Room, RoomEvent, Track, type RemoteTrack } from 'livekit-client';
import {
  CONTROL_TOPIC,
  encodeControlMessage,
  type CameraDevice,
  type Capabilities,
  type ControlMessage,
} from '@fastgarage/protocol';
import {
  completeUpload,
  endCustomerSession,
  fetchLivekitToken,
  presignUpload,
  putObject,
} from './api';
import { EndReporter, isPermanentUploadError } from './endReporter';
import {
  applyTorch,
  applyZoom,
  blobDimensions,
  captureCanvasFrame,
  captureViaImageCapture,
  detectFacingModes,
  detectPlatform,
  enumerateCameras,
  getImageCaptureCtor,
  normalizeContentType,
  readTrackCapabilities,
  reencodeToJpeg,
  sha256Hex,
  trackDeviceId,
} from './capture';
import { createCommandDispatcher, newRequestId, type CapturedPhoto } from './commands';
import {
  acquireCamera,
  acquireMicrophone,
  type CameraAcquisition,
  type CameraTarget,
} from './media';
import { UploadQueue, type UploadStatus } from './uploadQueue';

export type FacingMode = 'user' | 'environment';

export type ConnectionUiState = 'verbunden' | 'wiederverbinden' | 'getrennt';

// Camera and microphone acquisition live in ./media — split apart so that a
// microphone problem can never cost us the camera. Re-exported here because
// both were part of this module's surface before.
export {
  acquireCamera,
  acquireMicrophone,
  MediaUnavailableError,
  type CameraAcquisition,
  type CameraTarget,
} from './media';

/** Facing mode the browser actually delivered, else the requested one. */
function resolveFacing(track: MediaStreamTrack, fallback: FacingMode): FacingMode {
  const facing = track.getSettings().facingMode;
  return facing === 'user' || facing === 'environment' ? facing : fallback;
}

/** Builds the CAPABILITIES announcement from the live video track. */
export async function buildCapabilitiesMessage(
  track: MediaStreamTrack,
  cameras?: CameraDevice[],
): Promise<Capabilities> {
  const hardware = readTrackCapabilities(track);
  const list = cameras ?? (await enumerateCameras(track));
  const facingModes = await detectFacingModes(track, list);
  return {
    type: 'CAPABILITIES',
    requestId: newRequestId(),
    device: 'handy',
    platform: detectPlatform(),
    torch: hardware.torch,
    zoom: hardware.zoom,
    imageCapture: getImageCaptureCtor() !== null,
    facingModes,
    cameras: list,
    activeCameraId: trackDeviceId(track),
  };
}

export interface RuntimeCallbacks {
  /** Local preview stream changed (initial camera or camera switch). */
  onLocalStream(stream: MediaStream): void;
  /** Available cameras changed (initial scan, switch, USB camera plugged in). */
  onCameras(cameras: CameraDevice[], activeCameraId: string | null): void;
  /** True as soon as at least one remote participant (staff) is in the room. */
  onStaffPresence(connected: boolean): void;
  onConnection(state: ConnectionUiState): void;
  /** Final disconnect (room ended or user left) — show the Abschlussseite. */
  onDisconnected(): void;
  onUploadStatus(status: UploadStatus): void;
  onPointer(x: number, y: number): void;
  onHint(text: string): void;
}

/**
 * Orchestrates the live part of a customer session: camera, LiveKit room,
 * control-command handling and the photo upload queue. Created only AFTER
 * Kennzeichen + consent were submitted — no camera access happens before.
 */
export class SessionRuntime {
  private room: Room | null = null;
  private videoTrack: MediaStreamTrack | null = null;
  private audioTrack: MediaStreamTrack | null = null;
  private facing: FacingMode = 'environment';
  private cameras: CameraDevice[] = [];
  private activeCameraId: string | null = null;
  /** Guards against overlapping camera switches (staff click + picker + recovery). */
  private switching = false;
  private previewVideo: HTMLVideoElement | null = null;
  private audioContainer: HTMLElement | null = null;
  private wakeLock: WakeLockSentinel | null = null;
  private active = false;
  private disposed = false;
  /** Defers the customer-end API call until the upload queue drained —
   * ending the session server-side blocks further uploads (410). */
  private readonly endReporter = new EndReporter(
    () => this.queue.status.queued,
    () =>
      void endCustomerSession(this.session.sessionId, this.session.customerToken).catch(
        () => undefined, // Best-effort: staff can always end from the dashboard.
      ),
  );

  private readonly queue: UploadQueue;
  private readonly dispatcher = createCommandDispatcher({
    getVideoTrack: () => this.videoTrack,
    hasImageCapture: () => getImageCaptureCtor() !== null,
    captureViaImageCapture: (track) => captureViaImageCapture(track),
    captureViaCanvas: async (track) => captureCanvasFrame(track, await this.ensureCaptureVideo()),
    setTorch: (track, on) => applyTorch(track, on),
    setZoom: (track, zoom) => applyZoom(track, zoom),
    switchCamera: (target) => this.selectCamera(target),
    showPointer: (x, y) => this.callbacks.onPointer(x, y),
    showHint: (text) => this.callbacks.onHint(text),
    submitPhoto: (requestId, photo) => this.submitPhoto(requestId, photo),
    reportError: (requestId, reason) =>
      this.sendControl({ type: 'PHOTO_ERROR', requestId, reason }),
  });

  /** A camera was plugged in or removed — refresh the list and re-announce. */
  private readonly onDeviceChange = (): void => {
    if (this.disposed) return;
    void this.refreshCameras();
  };

  /** The published camera disappeared (USB cable pulled, device revoked).
   * Fall back to another camera so the session keeps running. */
  private readonly onTrackEnded = (): void => {
    if (!this.active || this.switching) return;
    void this.recoverFromEndedTrack();
  };

  private readonly onVisibility = (): void => {
    if (document.visibilityState === 'visible' && this.active) {
      void this.requestWakeLock();
    }
  };

  private readonly onBeforeUnload = (event: BeforeUnloadEvent): void => {
    if (!this.active) return;
    event.preventDefault();
    // Chrome still requires returnValue to show the confirmation dialog.
    event.returnValue = '';
  };

  constructor(
    private readonly session: { sessionId: string; customerToken: string },
    private readonly callbacks: RuntimeCallbacks,
  ) {
    this.queue = new UploadQueue({
      uploadJob: async (job) => {
        const presign = await presignUpload(session.sessionId, session.customerToken, {
          contentType: job.contentType,
          source: job.source,
          device: 'handy',
        });
        await putObject(presign.uploadUrl, job.blob, job.contentType);
        const complete = await completeUpload(session.sessionId, session.customerToken, {
          photoId: presign.photoId,
          sha256: job.sha256,
          width: job.width,
          height: job.height,
        });
        return { photoId: complete.photo.id };
      },
      onJobDone: (job, photoId) => {
        this.sendControl({
          type: 'PHOTO_DONE',
          requestId: job.requestId,
          photoId,
          source: job.source,
          device: 'handy',
          width: job.width,
          height: job.height,
        });
      },
      onStatus: (status) => {
        this.callbacks.onUploadStatus(status);
        this.endReporter.onQueueStatus();
      },
      // The session may end (either side) while photos are still queued —
      // presign/complete then answer 410/404 forever, so stop retrying.
      isPermanentError: isPermanentUploadError,
    });
  }

  get transferredCount(): number {
    return this.queue.status.transferred;
  }

  /**
   * Camera on, LiveKit connect, publish, announce capabilities, wake lock.
   *
   * `preAcquired` is the camera the caller already opened inside the user's
   * tap — see SessionFlow. Opening it here instead would put getUserMedia
   * behind a network round trip, and browsers that insist on a user gesture
   * (in-app WebViews, older iOS) then drop the request without ever showing a
   * prompt.
   */
  async start(preAcquired?: CameraAcquisition): Promise<void> {
    const cam = preAcquired ?? (await acquireCamera({ facing: 'environment' }));
    this.bindVideoTrack(cam.videoTrack);
    this.facing = resolveFacing(cam.videoTrack, 'environment');
    this.callbacks.onLocalStream(new MediaStream([cam.videoTrack]));

    // Intercom sound is optional: a denied or missing microphone leaves the
    // session running with picture only. acquireMicrophone never throws.
    this.audioTrack = await acquireMicrophone();

    const tokenRes = await fetchLivekitToken(this.session.sessionId, this.session.customerToken);
    const room = new Room({ adaptiveStream: true });
    this.room = room;
    this.registerRoomEvents(room);
    await room.connect(tokenRes.url, tokenRes.token, { autoSubscribe: true });

    await room.localParticipant.publishTrack(cam.videoTrack, {
      source: Track.Source.Camera,
      simulcast: false,
    });
    if (this.audioTrack) {
      await room.localParticipant.publishTrack(this.audioTrack, {
        source: Track.Source.Microphone,
      });
    }
    this.attachExistingRemoteAudio(room);
    // Labels are only readable after permission was granted, so the camera
    // list is built here — not before getUserMedia.
    await this.refreshCameras();

    navigator.mediaDevices?.addEventListener?.('devicechange', this.onDeviceChange);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    await this.requestWakeLock();
    this.active = true;
    this.callbacks.onConnection('verbunden');
    if (room.remoteParticipants.size > 0) {
      this.callbacks.onStaffPresence(true);
    }
  }

  /** The CameraScreen registers its preview <video> for canvas captures. */
  setPreviewVideo(video: HTMLVideoElement | null): void {
    this.previewVideo = video;
  }

  /** Customer-side camera picker: publish one exact video input. */
  async selectCameraById(deviceId: string): Promise<void> {
    await this.selectCamera({ deviceId });
  }

  /** User tapped „Beenden" (after confirm). */
  async end(): Promise<void> {
    this.active = false;
    if (this.room) {
      await this.room.disconnect().catch(() => undefined);
    } else {
      this.cleanupMedia();
      this.callbacks.onDisconnected();
    }
    this.endReporter.requestEnd();
  }

  /** Full teardown on unmount. Also stops the upload queue. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.active = false;
    this.cleanupMedia();
    this.queue.stop();
    const room = this.room;
    this.room = null;
    if (room) {
      void room.disconnect().catch(() => undefined);
    }
  }

  // ── internals ─────────────────────────────────────────────────────────────

  private registerRoomEvents(room: Room): void {
    room
      .on(RoomEvent.DataReceived, (payload, _participant, _kind, topic) => {
        if (topic !== CONTROL_TOPIC) return;
        this.dispatcher.handleData(payload);
      })
      .on(RoomEvent.TrackSubscribed, (track) => {
        this.attachRemoteTrack(track);
      })
      .on(RoomEvent.TrackUnsubscribed, (track) => {
        track.detach().forEach((el) => el.remove());
      })
      .on(RoomEvent.ParticipantConnected, () => {
        this.callbacks.onStaffPresence(true);
        // The staff may join (or fully reconnect) AFTER us — e.g. on the code
        // path the customer sits alone in the room until the staff enters the
        // code. Data-channel messages are not replayed by the server, so the
        // capabilities must be announced again for every newcomer.
        void this.announceCapabilities();
      })
      .on(RoomEvent.ParticipantDisconnected, () => {
        this.callbacks.onStaffPresence(room.remoteParticipants.size > 0);
      })
      .on(RoomEvent.Reconnecting, () => {
        this.callbacks.onConnection('wiederverbinden');
      })
      .on(RoomEvent.Reconnected, () => {
        this.callbacks.onConnection('verbunden');
        // After a full reconnect the dashboard may have been rebuilt and lost
        // our earlier announcement.
        void this.announceCapabilities();
      })
      .on(RoomEvent.Disconnected, () => {
        // Final disconnect (staff ended the room, or we left). Keep the upload
        // queue draining so already-taken photos still reach the server.
        this.active = false;
        this.cleanupMedia();
        this.callbacks.onConnection('getrennt');
        this.callbacks.onDisconnected();
      });
  }

  /** Staff audio: attach subscribed audio tracks to hidden <audio> elements. */
  private attachRemoteTrack(track: RemoteTrack): void {
    if (track.kind !== Track.Kind.Audio) return;
    const container = this.ensureAudioContainer();
    const element = track.attach();
    container.appendChild(element);
  }

  private attachExistingRemoteAudio(room: Room): void {
    for (const participant of room.remoteParticipants.values()) {
      for (const publication of participant.trackPublications.values()) {
        const track = publication.track;
        if (track) this.attachRemoteTrack(track as RemoteTrack);
      }
    }
  }

  private ensureAudioContainer(): HTMLElement {
    if (!this.audioContainer) {
      const container = document.createElement('div');
      container.setAttribute('data-remote-audio', '');
      container.style.display = 'none';
      document.body.appendChild(container);
      this.audioContainer = container;
    }
    return this.audioContainer;
  }

  private sendControl(message: ControlMessage): void {
    const room = this.room;
    if (!room) return;
    void room.localParticipant
      .publishData(encodeControlMessage(message), { reliable: true, topic: CONTROL_TOPIC })
      .catch(() => undefined);
  }

  private async announceCapabilities(): Promise<void> {
    const track = this.videoTrack;
    if (!track || !this.room) return;
    try {
      this.sendControl(await buildCapabilitiesMessage(track, this.cameras));
    } catch {
      // Capability detection must never break the session.
    }
  }

  /** Hash + measure + enqueue a captured blob; PHOTO_DONE is sent on upload. */
  private async submitPhoto(requestId: string, photo: CapturedPhoto): Promise<void> {
    let blob = photo.blob;
    let contentType = normalizeContentType(blob);
    if (!contentType) {
      blob = await reencodeToJpeg(blob);
      contentType = 'image/jpeg';
    }
    const [sha256, dims] = await Promise.all([sha256Hex(blob), blobDimensions(blob)]);
    this.queue.enqueue({
      requestId,
      blob,
      contentType,
      source: photo.source,
      width: dims.width,
      height: dims.height,
      sha256,
    });
  }

  private bindVideoTrack(track: MediaStreamTrack): void {
    this.videoTrack = track;
    this.activeCameraId = trackDeviceId(track);
    track.addEventListener('ended', this.onTrackEnded);
  }

  private unbindVideoTrack(): MediaStreamTrack | null {
    const track = this.videoTrack;
    track?.removeEventListener('ended', this.onTrackEnded);
    this.videoTrack = null;
    return track;
  }

  /** Re-scans the video inputs, tells the UI and re-announces CAPABILITIES. */
  private async refreshCameras(announce = true): Promise<CameraDevice[]> {
    if (this.disposed) return this.cameras;
    const cameras = await enumerateCameras(this.videoTrack);
    this.cameras = cameras;
    this.activeCameraId = trackDeviceId(this.videoTrack);
    this.callbacks.onCameras(cameras, this.activeCameraId);
    if (announce) {
      await this.announceCapabilities();
    }
    return cameras;
  }

  /**
   * SWITCH_CAMERA / customer picker: republish with another video input and
   * re-announce the capabilities (an external camera usually has no torch,
   * no zoom and no working ImageCapture).
   *
   * `deviceId` selects one exact camera — the only way to reach an external
   * USB/UVC camera, which reports no facing mode. Without it the facing mode
   * flips, as before.
   */
  private async selectCamera(
    target: { deviceId?: string; facing?: FacingMode },
    force = false,
  ): Promise<void> {
    const room = this.room;
    if (!room || this.switching) return;

    const previousFacing = this.facing;
    const previousDeviceId = this.activeCameraId;
    const requestedFacing = target.deviceId
      ? (this.cameras.find((c) => c.deviceId === target.deviceId)?.facingMode ?? previousFacing)
      : (target.facing ?? (previousFacing === 'environment' ? 'user' : 'environment'));

    if (!force) {
      if (target.deviceId) {
        if (target.deviceId === previousDeviceId) return;
      } else if (requestedFacing === previousFacing) {
        return;
      }
    }

    this.switching = true;
    try {
      const old = this.unbindVideoTrack();
      if (old) {
        try {
          await room.localParticipant.unpublishTrack(old, true);
        } catch {
          // Keep going — we stop the raw track below in any case.
        }
        old.stop();
      }

      // Requested camera first, then the previous one so a failed switch never
      // leaves the customer without video.
      const attempts: CameraTarget[] = [
        target.deviceId
          ? { deviceId: target.deviceId, facing: requestedFacing }
          : { facing: requestedFacing },
      ];
      if (previousDeviceId && previousDeviceId !== target.deviceId) {
        attempts.push({ deviceId: previousDeviceId, facing: previousFacing });
      }
      attempts.push({ facing: previousFacing });

      for (const attempt of attempts) {
        let acquired: MediaStreamTrack | null = null;
        try {
          const cam = await acquireCamera(attempt);
          acquired = cam.videoTrack;
          // The session may have been torn down while getUserMedia was open —
          // never leave a camera running that nobody owns.
          if (this.disposed || this.room !== room) {
            acquired.stop();
            return;
          }
          this.bindVideoTrack(acquired);
          this.facing = resolveFacing(acquired, attempt.facing);
          await room.localParticipant.publishTrack(acquired, {
            source: Track.Source.Camera,
            simulcast: false,
          });
          this.callbacks.onLocalStream(new MediaStream([acquired]));
          return;
        } catch {
          // Release this candidate before trying the next one.
          if (acquired) {
            if (this.videoTrack === acquired) this.unbindVideoTrack();
            acquired.stop();
          }
        }
      }
    } finally {
      this.switching = false;
      // Labels, ids and hardware features changed with the new track, so the
      // UI is updated in any case; CAPABILITIES follow whenever a track was
      // published (with none, there is nothing to announce).
      await this.refreshCameras();
    }
  }

  /** Recovery after the active camera vanished: pick any remaining one. */
  private async recoverFromEndedTrack(): Promise<void> {
    const dead = this.activeCameraId;
    const cameras = await this.refreshCameras(false);
    const candidate =
      cameras.find((c) => c.deviceId !== dead && c.facingMode === 'environment') ??
      cameras.find((c) => c.deviceId !== dead);
    await this.selectCamera(
      candidate ? { deviceId: candidate.deviceId } : { facing: 'environment' },
      true,
    );
  }

  /** Video element used for canvas captures: the visible preview when ready,
   * otherwise a temporary off-screen element playing the local track. */
  private async ensureCaptureVideo(): Promise<HTMLVideoElement> {
    const preview = this.previewVideo;
    if (preview && preview.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      return preview;
    }
    const track = this.videoTrack;
    if (!track) throw new Error('no active video track');
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = new MediaStream([track]);
    await video.play();
    if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      await new Promise<void>((resolve) => {
        const done = (): void => {
          video.removeEventListener('loadeddata', done);
          resolve();
        };
        video.addEventListener('loadeddata', done);
        setTimeout(done, 3_000);
      });
    }
    return video;
  }

  private async requestWakeLock(): Promise<void> {
    try {
      this.wakeLock = (await navigator.wakeLock?.request('screen')) ?? null;
    } catch {
      // Wake lock is best-effort (unsupported browsers, low battery, …).
    }
  }

  private cleanupMedia(): void {
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    navigator.mediaDevices?.removeEventListener?.('devicechange', this.onDeviceChange);
    if (this.wakeLock) {
      void this.wakeLock.release().catch(() => undefined);
      this.wakeLock = null;
    }
    this.unbindVideoTrack()?.stop();
    this.audioTrack?.stop();
    this.audioTrack = null;
    if (this.audioContainer) {
      this.audioContainer.remove();
      this.audioContainer = null;
    }
  }
}
